import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { getMyHunts, listHunts } from '../services/api';

export function ProfilePage() {
  const { user, isAuthenticated, isCheckingAuth } = useAuthStore();

  const { data: huntsData } = useQuery({
    queryKey: ['hunts'],
    queryFn: listHunts,
    enabled: isAuthenticated,
  });

  const { data: myHunts, isLoading } = useQuery({
    queryKey: ['myHunts'],
    queryFn: getMyHunts,
    enabled: isAuthenticated,
  });

  if (isCheckingAuth) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse text-text-light">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const huntsMap = new Map(huntsData?.hunts.map(h => [h.huntId, h]) || []);

  const joinedHunts = myHunts?.hunts || [];
  const completedHunts = joinedHunts.filter(h => h.completedAt);
  const inProgressHunts = joinedHunts.filter(h => !h.completedAt && h.currentClueIndex > 0);
  const waitingHunts = joinedHunts.filter(h => h.currentClueIndex === 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      {/* Profile Header */}
      <div className="card mb-6 sm:mb-8">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-xl sm:text-2xl font-bold text-primary">
              {user?.email.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-text-dark truncate">{user?.email.split('@')[0]}</h1>
            <p className="text-sm sm:text-base text-text-light truncate">{user?.email}</p>
            {user?.isAdmin && (
              <span className="inline-block mt-1 text-xs bg-secondary text-white px-2 py-0.5 rounded">
                Admin
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8">
        <div className="card text-center p-3 sm:p-6">
          <div className="text-2xl sm:text-3xl font-bold text-primary">{joinedHunts.length}</div>
          <div className="text-xs sm:text-sm text-text-light">Joined</div>
        </div>
        <div className="card text-center p-3 sm:p-6">
          <div className="text-2xl sm:text-3xl font-bold text-secondary">{completedHunts.length}</div>
          <div className="text-xs sm:text-sm text-text-light">Completed</div>
        </div>
        <div className="card text-center p-3 sm:p-6">
          <div className="text-2xl sm:text-3xl font-bold text-accent">{inProgressHunts.length}</div>
          <div className="text-xs sm:text-sm text-text-light">In Progress</div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-text-light">Loading your hunts...</div>
      ) : (
        <>
          {/* In Progress Hunts */}
          {inProgressHunts.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-bold text-primary mb-4">In Progress</h2>
              <div className="space-y-4">
                {inProgressHunts.map(progress => {
                  const hunt = huntsMap.get(progress.huntId);
                  return (
                    <Link
                      key={progress.huntId}
                      to={`/h/${progress.huntId}`}
                      className="card block hover:shadow-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-text-dark">
                            {hunt?.title || progress.huntId}
                          </h3>
                          <p className="text-sm text-text-light">
                            {progress.solvedCount} of {hunt?.clueCount || '?'} clues solved
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-text-light">
                            Started {progress.startedAt ? formatDate(progress.startedAt) : 'recently'}
                          </div>
                          <span className="text-primary font-medium">Continue</span>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{
                            width: `${hunt?.clueCount ? (progress.solvedCount / hunt.clueCount) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Completed Hunts */}
          {completedHunts.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-bold text-secondary mb-4">Completed</h2>
              <div className="space-y-4">
                {completedHunts.map(progress => {
                  const hunt = huntsMap.get(progress.huntId);
                  return (
                    <Link
                      key={progress.huntId}
                      to={`/h/${progress.huntId}`}
                      className="card block hover:shadow-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-text-dark">
                            {hunt?.title || progress.huntId}
                          </h3>
                          <p className="text-sm text-green-600">
                            Completed {formatDate(progress.completedAt!)}
                          </p>
                        </div>
                        <span className="badge bg-green-100 text-green-800">Completed</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Waiting Hunts (joined but not started) */}
          {waitingHunts.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-bold text-accent mb-4">Waiting to Start</h2>
              <div className="space-y-4">
                {waitingHunts.map(progress => {
                  const hunt = huntsMap.get(progress.huntId);
                  return (
                    <Link
                      key={progress.huntId}
                      to={`/h/${progress.huntId}`}
                      className="card block hover:shadow-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-text-dark">
                            {hunt?.title || progress.huntId}
                          </h3>
                          <p className="text-sm text-text-light">
                            Joined {formatDate(progress.joinedAt || progress.startedAt || '')}
                          </p>
                        </div>
                        <span className="text-primary font-medium">Start Hunt</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* No hunts yet */}
          {joinedHunts.length === 0 && (
            <div className="card text-center py-8">
              <p className="text-text-light mb-4">You haven't joined any hunts yet.</p>
              <Link to="/" className="btn-primary inline-block">
                Browse Hunts
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
